import React, {FC, ReactNode, useContext, useState} from 'react';
import styled from "styled-components";
import {FlyHourPriceCalculatorField} from "./FlyHourPriceCalculatorField";
import {Row, Col} from "antd";
import {
  IFlyHourCalculatorCategory,
  IFlyHourCalculatorSubCategory,
  IFlyHourCalculatorSettingType
} from "../../../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {CustomButton} from "../../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../../../intl/commonMessages/CommonButtonMessages";
import {FlyHourPriceCalculatorAddFieldModal} from "./FlyHourPriceCalculatorAddFieldModal";
import {FlyHourPriceCalculatorAddPeriodModal} from "./FlyHourPriceCalculatorAddPeriodModal";
import {FlyHourPriceContext} from "./context/FlyHourPriceContextProvider";
import {FlyHourPriceDefaultSubcategoryEnum, FlyHourPriceDefaultSettingTypeEnum} from "./types/flyHourPriceSettingEnums";
import {useFlyPriceCategorySetter} from "./hooks/useFlyPriceCategorySetter";
import {FlyHourPriceCalculatorSettingTypeEnum} from "./types/flyHourPriceSettingEnums";
import {FlyHourPriceMessages} from "../../../../../../intl/commonMessages/FlyHourPriceMessages";
import {useNavigate} from "react-router-dom";
import {Route} from "../../../../../../config/acl/routes/Route";
import {routes} from "../../../../../../config/acl/routes";
import {FONTS} from "../../../../../../utils/constans/styles/fonts";
import PlusIcon from "../../../../../../media/icons/plus.svg?react";

const BlackPlusIcon = styled(PlusIcon)`
  fill: #000000 !important;
  
  rect {
    fill: #000000 !important;
  }
`;

const StyledWrapper = styled.div`
  background: #F9F9F9;
  border: 1px solid #D9D9D9;
  border-radius: 10px;
  padding: 20px 16px 24px 16px;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TitleContent = styled.div`
  display: flex;
  flex-direction: row;

  div {
    font-size: 14px;
    line-height: 17px;

    :nth-child(1) {
      font-weight: 500;
      color: #000000;
    }

    :nth-child(2) {
      font-weight: 400;
      color: #E99700;
      margin-left: 24px;
    }
  }
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const FlyHourPriceCalculatorSubgroupMessages = defineMessages({
  marginTaxesDisabled: {
    id: "flyHourPriceCalculatorSubgroup.marginTaxesDisabled",
    defaultMessage: "Specify company margin above"
  },
  addMarginBlock: {
    id: "flyHourPriceCalculator.addMarginBlock",
    defaultMessage: "Add Margin Block"
  },
  removeMarginBlock: {
    id: "flyHourPriceCalculator.removeMarginBlock",
    defaultMessage: "Remove Margin Block"
  },
  addAnotherBlock: {
    id: "flyHourPriceCalculator.addAnotherBlock",
    defaultMessage: "Добавить еще блок"
  },
  maxMarginBlocks: {
    id: "flyHourPriceCalculator.maxMarginBlocks",
    defaultMessage: "Maximum 7 margin blocks"
  },
})

interface IFlyHourPriceCalculatorSubgroup {
  category: IFlyHourCalculatorCategory;
  subcategory: IFlyHourCalculatorSubCategory;
  isOnlyOne: boolean;
  isFieldsCountChangeable: boolean;
}

export const FlyHourPriceCalculatorSubgroup: FC<IFlyHourPriceCalculatorSubgroup> = ({
                                                                                      category,
                                                                                      subcategory,
                                                                                      isOnlyOne,
                                                                                      isFieldsCountChangeable
                                                                                    }) => {
  const intl = useIntl();
  const categorySetter = useFlyPriceCategorySetter(category.keyName);
  
  // Проверяем, является ли это категорией маржи с блоками
  const isMarginCategory = subcategory.keyName === FlyHourPriceDefaultSubcategoryEnum.SUB_CATEGORY_MARGIN_COMPANY_MARGIN;
  const marginBlocks = subcategory.settingTypes.filter(st => 
    st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT 
    || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT + '_')
  );
  const canAddMarginBlock = marginBlocks.length < 7;

  // Проверяем, является ли это сезонным коэффициентом (Low dates или High season)
  const isSeasonalCoefficient = subcategory.keyName === 'sub_category_coefficient_low_dates' 
    || subcategory.keyName === 'sub_category_coefficient_high_season';
  
  // Проверяем, является ли это блоком Event Calendar
  const isEventCalendarBlock = subcategory.keyName === 'sub_category_coefficient_event_calendar';
  const navigate = useNavigate();
  
  // Находим основное поле значения (без дат) и периоды (с датами)
  const mainValueField = subcategory.settingTypes.find(st => 
    (st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES 
      || st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON)
    && !st.setting.dateFrom && !st.setting.dateTo
  );
  const periodFields = subcategory.settingTypes.filter(st => 
    (st.setting.dateFrom || st.setting.dateTo)
    && (st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES 
      || st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON
      || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES + '_')
      || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON + '_'))
  );

  const addMarginBlock = () => {
    if (!categorySetter || !canAddMarginBlock) return;
    
    const newBlock: IFlyHourCalculatorSettingType = {
      keyName: `${FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT}_${Date.now()}`,
      label: intl.formatMessage(FlyHourPriceMessages[FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT]) + ` (${marginBlocks.length + 1})`,
      isEnabled: true,
      isDefault: false,
      setting: {
        value: 0,
        specificType: FlyHourPriceCalculatorSettingTypeEnum.PERCENT,
        fromHours: marginBlocks.length > 0 ? (marginBlocks[marginBlocks.length - 1].setting.toHours ?? 0) : 0,
        toHours: undefined, // Бесконечность для последнего блока
      },
      type: FlyHourPriceCalculatorSettingTypeEnum.PERCENT,
    };

    categorySetter(c => {
      return {
        ...c, subcategories: c.subcategories.map(sc => {
          if (sc.keyName !== subcategory.keyName)
            return sc;
          return {
            ...sc, settingTypes: [...sc.settingTypes, newBlock]
          }
        })
      }
    });
  };

  const removeMarginBlock = (blockKeyName: string) => {
    if (!categorySetter) return;
    
    categorySetter(c => {
      return {
        ...c, subcategories: c.subcategories.map(sc => {
          if (sc.keyName !== subcategory.keyName)
            return sc;
          return {
            ...sc, settingTypes: sc.settingTypes.filter(st => st.keyName !== blockKeyName)
          }
        })
      }
    });
  };

  const [isAddPeriodModalVisible, setIsAddPeriodModalVisible] = useState(false);

  // Если это блок Event Calendar, рендерим специальный информационный блок
  if (isEventCalendarBlock) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px 41px 20px 30px',
        width: '100%',
        minHeight: 165,
        background: '#E2F1FF',
        borderRadius: 10,
        boxSizing: 'border-box',
      }}>
        <div style={{
          fontFamily: FONTS.TEXT_REGULAR_INTER,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: 16,
          lineHeight: '150%',
          color: '#000000',
          textAlign: 'left',
          maxWidth: '100%',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}>
          {intl.formatMessage(
            {id: "flyHourPriceCalculator.eventCalendarInfo", defaultMessage: "На итоговую стоимость также влияют события из {link} (надбавки по аэропортам и сезонам)"},
            {
              link: (
                <span
                  onClick={() => navigate(routes[Route.eventCalendar].path)}
                  style={{
                    color: '#1890FF',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    display: 'inline',
                  }}
                >
                  {intl.formatMessage({id: "flyHourPriceCalculator.eventCalendarLink", defaultMessage: "Календаря событий"})}
                </span>
              )
            }
          )}
        </div>
      </div>
    );
  }

  return (
    <FlyHourPriceCalculatorSubgroupWrapper isOnlyOne={isOnlyOne} title={subcategory.label} category={category}
                                           subcategory={subcategory}
                                           isFieldsCountChangeable={isFieldsCountChangeable}
                                           isMarginCategory={isMarginCategory}
                                           canAddMarginBlock={canAddMarginBlock}
                                           onAddMarginBlock={addMarginBlock}
                                           onRemoveMarginBlock={removeMarginBlock}>
      {subcategory.settingTypes.map((st, index) => {
        const isMarginBlockItem = st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT 
          || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_AMOUNT + '_');
        
        // Пропускаем периоды сезонных коэффициентов - они отображаются как чипы
        const isPeriodField = (st.setting.dateFrom || st.setting.dateTo) 
          && (st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES 
            || st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON
            || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES + '_')
            || st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON + '_'));
        
        // Пропускаем поле urgency, если есть блоки margin (оно будет показано внутри блока margin)
        const isUrgencyField = st.keyName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_MARGIN_URGENCY_DISCOUNT;
        if (isUrgencyField && isMarginCategory && marginBlocks.length > 0) {
          return null;
        }
        
        if (isPeriodField) {
          return null;
        }
        
        return (
          <React.Fragment key={st.keyName}>
            <FlyHourPriceCalculatorField settingType={st} category={category} subcategory={subcategory}/>
            {isMarginCategory && isMarginBlockItem && marginBlocks.length > 1 && (
              <Col span={24} style={{marginBottom: 8}}>
                <CustomButton 
                  type="link" 
                  danger 
                  onClick={() => removeMarginBlock(st.keyName)}
                  style={{marginLeft: 0, paddingLeft: 0}}
                >
                  {intl.formatMessage(FlyHourPriceCalculatorSubgroupMessages.removeMarginBlock)}
                </CustomButton>
              </Col>
            )}
          </React.Fragment>
        );
      })}
      {isSeasonalCoefficient && mainValueField && (
        <>
          <Col span={24} style={{marginTop: 12}}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}>
              {periodFields.map((periodField) => {
                // Helper function to parse date string (format: YYYY-MM-DD)
                const parseDateString = (dateStr: string | undefined): { year: number, month: number, day: number } | null => {
                  if (!dateStr) return null;
                  const parts = dateStr.split('-');
                  if (parts.length === 3) {
                    // Format: YYYY-MM-DD
                    return { year: parseInt(parts[0]), month: parseInt(parts[1]), day: parseInt(parts[2]) };
                  }
                  return null;
                };
                
                const formatDateRange = () => {
                  let dateFromStr = '';
                  let dateToStr = '';
                  
                  const dateFromParsed = parseDateString(periodField.setting.dateFrom);
                  const dateToParsed = parseDateString(periodField.setting.dateTo);
                  
                  if (dateFromParsed) {
                    const yearShort = String(dateFromParsed.year).slice(-2);
                    dateFromStr = `${dateFromParsed.day}.${String(dateFromParsed.month).padStart(2, '0')}.${yearShort}`;
                  }
                  
                  if (dateToParsed) {
                    const yearShort = String(dateToParsed.year).slice(-2);
                    dateToStr = `${dateToParsed.day}.${String(dateToParsed.month).padStart(2, '0')}.${yearShort}`;
                  }
                  
                  if (dateFromStr && dateToStr) {
                    return `${dateFromStr}–${dateToStr}`;
                  } else if (dateFromStr) {
                    return dateFromStr;
                  } else if (dateToStr) {
                    return dateToStr;
                  }
                  
                  return '';
                };

                return (
                  <div
                    key={periodField.keyName}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4px 12px',
                      gap: 8,
                      background: '#ECEDF0',
                      borderRadius: 8,
                      height: 32,
                      minWidth: 160,
                    }}
                  >
                    <div style={{
                      fontSize: 12,
                      lineHeight: '24px',
                      color: '#000000',
                      textAlign: 'center',
                    }}>
                      {formatDateRange()}
                    </div>
                    <div
                      onClick={() => {
                        if (categorySetter) {
                          categorySetter(c => {
                            return {
                              ...c, subcategories: c.subcategories.map(sc => {
                                if (sc.keyName !== subcategory.keyName)
                                  return sc;
                                return {
                                  ...sc, settingTypes: sc.settingTypes.filter(st => st.keyName !== periodField.keyName)
                                }
                              })
                            }
                          });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        marginLeft: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L11 11M11 1L1 11" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                );
              })}
              <div
                onClick={() => setIsAddPeriodModalVisible(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  background: '#ECEDF0',
                  border: 'none',
                  borderRadius: 8,
                  padding: periodFields.length === 0 ? '4px 12px' : '4px',
                  height: 32,
                  fontSize: 12,
                  color: '#000000',
                  cursor: 'pointer',
                  minWidth: periodFields.length === 0 ? 'auto' : 32,
                  textAlign: 'center',
                }}
              >
                {periodFields.length === 0 && (
                  <span style={{
                    fontFamily: FONTS.TEXT_REGULAR_INTER,
                    fontSize: 12,
                    lineHeight: '24px',
                    textAlign: 'center',
                  }}>
                    {intl.formatMessage({id: "flyHourPriceCalculator.addNewPeriod", defaultMessage: "Добавить новый период"})}
                  </span>
                )}
                <BlackPlusIcon style={{width: 16, height: 16}} />
              </div>
            </div>
          </Col>
          <FlyHourPriceCalculatorAddPeriodModal
            isModalVisible={isAddPeriodModalVisible}
            setIsModalVisible={setIsAddPeriodModalVisible}
            category={category}
            subCategory={subcategory}
            mainValueField={mainValueField}
          />
        </>
      )}
    </FlyHourPriceCalculatorSubgroupWrapper>
  );
};

interface IFlyHourPriceCalculatorSubgroupWrapper extends IFlyHourPriceCalculatorSubgroup {
  title: string;
  children: ReactNode;
  isMarginCategory?: boolean;
  canAddMarginBlock?: boolean;
  onAddMarginBlock?: () => void;
  onRemoveMarginBlock?: (blockKeyName: string) => void;
}

const FlyHourPriceCalculatorSubgroupWrapper: FC<IFlyHourPriceCalculatorSubgroupWrapper> = ({
                                                                                                                               isOnlyOne,
                                                                                                                               title,
                                                                                                                               category,
                                                                                                                               subcategory,
                                                                                                                               isFieldsCountChangeable,
                                                                                                                               children,
                                                                                                                               isMarginCategory = false,
                                                                                                                               canAddMarginBlock = false,
                                                                                                                               onAddMarginBlock,
                                                                                                                               onRemoveMarginBlock
                                                                                                                             }) => {
  const intl = useIntl();
  const {isMarginTaxesDisabled} = useContext(FlyHourPriceContext);
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);

  return <>
    {isOnlyOne
      ? <EmptyWrapper>{children}</EmptyWrapper>
      : <StyledWrapper>
        <Title>
          <TitleContent>
            <div>{title}</div>
            {subcategory.keyName === FlyHourPriceDefaultSubcategoryEnum.SUB_CATEGORY_MARGIN_TAXES && isMarginTaxesDisabled &&
              <div>{intl.formatMessage(FlyHourPriceCalculatorSubgroupMessages.marginTaxesDisabled)}</div>}
          </TitleContent>
          <div style={{display: 'flex', gap: 8}}>
            {isFieldsCountChangeable && !isMarginCategory &&
              <CustomButton type={'link'} onClick={() => setIsAddFieldOpen(true)}>
                + {intl.formatMessage(CommonButtonMessages.add)}
              </CustomButton>}
          </div>
        </Title>
        <Row gutter={8}>{children}</Row>
        {isMarginCategory && canAddMarginBlock && onAddMarginBlock && (
          <div style={{width: '100%', marginTop: 16}}>
            <button
              onClick={onAddMarginBlock}
              style={{
                width: '100%',
                background: '#FFFFFF',
                border: '1px solid #D9DAD9',
                borderRadius: '5px',
                padding: '12px 16px',
                fontFamily: 'Inter, sans-serif',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '120%',
                color: '#000000',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F5F5F5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#FFFFFF';
              }}
            >
              <span>+</span>
              <span>{intl.formatMessage(FlyHourPriceCalculatorSubgroupMessages.addAnotherBlock)}</span>
            </button>
          </div>
        )}
      </StyledWrapper>}

    {isAddFieldOpen &&
      <FlyHourPriceCalculatorAddFieldModal isModalVisible={isAddFieldOpen} setIsModalVisible={setIsAddFieldOpen}
                                           category={category} subCategory={subcategory}/>}
  </>
}
